
import { defineComponent, ref, reactive, toRefs, onMounted, computed, nextTick, watch } from 'vue'
import * as echarts from 'echarts'
import Nodata from '@/components/page/nodata.vue'
import f10ApiUS from '@/api/f10ApiUS'
import { useRoute } from 'vue-router'
import { isLocalDev } from '@/api/config'
import { InfoCircleOutlined } from '@ant-design/icons-vue'
export default defineComponent({
  props: {
    itemdata: {
      type: Object,
      default: () => ({})
    },
    pageSourceData: {
      type: Object,
      default: () => ({})
    },
    theme: {
      type: String,
      default: () => ''
    },
    token: {
      type: String,
      default: () => ''
    }
  },
  components: { Nodata, InfoCircleOutlined },
  setup(props, context) {
    const route = useRoute()
    const state = reactive({
      financialData: [] as any[],
      spinning: true,
      showYearOverYear: false,
      activeIndicator: 'mainIncomePs',
      activeReportType: 0,
      reportCate: 1,
      showImg: true,
      simpleImage: '',
      indicators: [
        // 每股指标
        { key: 'group-title', title: '每股指标', indent: 0, isGroupTitle: true },
        { key: 'mainIncomePs', title: '每股营业收入', indent: 0 },
        { key: 'basicEps', title: '每股收益-基本', indent: 0 },
        { key: 'dilutedEps', title: '每股收益-稀释', indent: 0 },
        { key: 'netAssetPs', title: '每股净资产', indent: 0 },
        { key: 'ncfFromOaPs', title: '每股现金流', indent: 0 },

        // 偿债能力
        { key: 'group-title', title: '偿债能力', indent: 0, isGroupTitle: true },
        { key: 'currentRatio', title: '流动比率', indent: 0 },
        { key: 'quickRatio', title: '速动比率', indent: 0 },
        { key: 'equityRatio', title: '产权比率', indent: 0 },
        { key: 'equityAtsopcToTotalLiab', title: '归属母公司股东权益/负债总计', indent: 0 },

        // 营运能力
        { key: 'group-title', title: '营运能力', indent: 0, isGroupTitle: true },
        { key: 'operatingCycle', title: '营业周期', indent: 0 },
        { key: 'inventoryTurnoverDays', title: '存货周转天数', indent: 0 },
        { key: 'inventoryTurnover', title: '存货周转率', indent: 0 },
        { key: 'receivableTurnoverDays', title: '应收账款周转天数', indent: 0 },
        { key: 'accountReceivableTurnover', title: '应收账款周转率', indent: 0 },
        { key: 'currentAssetTurnover', title: '流动资金周转率', indent: 0 },
        { key: 'fixedAssetTurnoverRatio', title: '固定资产周转率', indent: 0 },
        { key: 'totalCapitalTurnover', title: '总资产周转率', indent: 0 }
      ],
      chartInstance: null as echarts.ECharts | null
    })

    // 计算报告类型数据
    const reportTypes = computed(() => {
      const baseTypes = [
        { label: '按报告期', value: 0 },
        { label: '年报', value: 4 },
        { label: '三季报', value: 3 },
        { label: '中报', value: 2 },
        { label: '一季报', value: 1 }
      ]

      // 当reportCate === 2（单季模式）时，修改标签
      if (state.reportCate === 2) {
        return baseTypes.map(item => {
          if (item.value === 4) {
            return { ...item, label: '四季度' }
          } else if (item.value === 2) {
            return { ...item, label: '二季度' }
          }
          return item
        })
      }

      return baseTypes
    })

    // 计算报告期数据
    const reportPeriods = computed(() => {
      if (!state.financialData.length) return []

      // 按报告日期排序（从大到小，最新的在前）
      const sortedData = state.financialData.slice().sort((a, b) =>
        new Date(b.reportDate).getTime() - new Date(a.reportDate).getTime()
      )

      // 根据选择的报告类型过滤
      let filteredData = sortedData;
      if (state.activeReportType !== 0) {
        filteredData = sortedData.filter(item => item.reportType === state.activeReportType)
      }

      // 无论哪种报告类型，都只取前11条（最新的11条）
      console.log(filteredData.filter((_, index) => index < 11), 11111)
      return filteredData.filter((_, index) => index < 11);
    })

    // 切换报告类型处理
    const handleReportTypeChange = () => {
      // 确保图表更新
      updateChart()
    }

    // 切换同比显示
    const toggleYearOverYear = () => {
      state.showYearOverYear = !state.showYearOverYear
      updateChart() // 切换同比时更新图表
      // 切换同比/环比后重新请求
      loadData()
    }

    // 切换单季开关：未选中时reportCate为1，选中时reportCate为2
    const toggleSingleQuarter = () => {
      state.reportCate = state.reportCate === 1 ? 2 : 1
      loadData()
    }

    // 切换报告期类别：1 累计；2 单季
    const toggleReportCate = () => {
      state.reportCate = state.reportCate === 1 ? 2 : 1
      loadData()
    }

    const handleReportCateChange = () => {
      loadData()
    }

    // 选择指标
    const selectIndicator = (item: any) => {
      if (item.isGroupTitle) return
      state.activeIndicator = item.key
      updateChart()
    }

    // 获取缩进类名
    const getIndentClass = (indent: number) => {
      if (indent === 1) return 'indent1'
      if (indent === 2) return 'indent2'
      return ''
    }

    // 获取指标值
    const getIndicatorValue = (key: string, period: any) => {
      return period[key]?.value ?? null
    }

    // 获取同比值
    const getYoyValue = (key: string, period: any) => {
      return period[key]?.yoy ?? null
    }

    // 获取同比样式类
    const getYoyClass = (key: string, period: any) => {
      const yoy = getYoyValue(key, period)
      if (yoy === null || yoy === undefined) return ''
      return yoy >= 0 ? 'yoy-positive' : 'yoy-negative'
    }
    watch(
      () => route.query.theme,
      (theme: any) => {
        if (theme === 'dark') {
          state.simpleImage = require('@/assets/nodata_dark_v.png')
        } else {
          state.simpleImage = require('@/assets/nodata_light_v.png')
        }
      },
      { immediate: true }
    )
    // 格式化数值 - 修改为无值时显示 "--"
    const formatValue = (value: number | null) => {
      if (value === null) return '--'

      // 处理负数的绝对值
      const absValue = Math.abs(value)
      const isNegative = value < 0

      let formattedValue = ''
      if (absValue >= 1000000000000) {
        formattedValue = (absValue / 1000000000000).toFixed(2) + '万亿'
      } else if (absValue >= 100000000) {
        formattedValue = (absValue / 100000000).toFixed(2) + '亿'
      } else if (absValue >= 10000) {
        formattedValue = (absValue / 10000).toFixed(2) + '万'
      } else {
        formattedValue = absValue.toFixed(2)
      }

      // 如果是负数，添加负号
      return isNegative ? '-' + formattedValue : formattedValue
    }

    // 格式化同比 - 修改为无值时显示 "--"，去除+/-符号
    const formatYoy = (yoy: number | null) => {
      if (yoy === null || yoy === undefined) return '--'
      const value = yoy * 100
      return value.toFixed(2) + '%'
    }


    // 格式化日期
    const formatDate = (timestamp: number) => {
      const date = new Date(timestamp)
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
    }

    // 根据statementYear和reportType生成美股报告期日期
    const getReportDate = (statementYear: number, reportType: number) => {
      let month = 3
      let day = 31

      switch (reportType) {
        case 1: // 一季报 3-31
          month = 3
          day = 31
          break
        case 2: // 二季报 6-30
          month = 6
          day = 30
          break
        case 3: // 三季报 9-30
          month = 9
          day = 30
          break
        case 4: // 四季报 12-31
          month = 12
          day = 31
          break
      }

      return `${statementYear}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
    }

    // 初始化图表
    const initChart = () => {
      const chartDom = document.getElementById('chartContainerzyzb')
      if (chartDom) {
        state.chartInstance = echarts.init(chartDom)
        updateChart()
      }
    }

    // 更新图表
    const updateChart = () => {
      if (!state.chartInstance) return

      // 对报告期按日期升序排序（从小到大）
      const sortedPeriods = reportPeriods.value.slice().sort((a, b) =>
        new Date(a.reportDate).getTime() - new Date(b.reportDate).getTime()
      )
      console.log(sortedPeriods, 22222)
      const indicatorData = sortedPeriods.map(period => ({
        date: formatDate(period.reportDate),
        value: getIndicatorValue(state.activeIndicator, period),
        dataTime: getReportDate(period.statementYear, period.reportType),
        yoy: getYoyValue(state.activeIndicator, period)
      }))

      console.log('indicatorData:', indicatorData) // 调试用
      console.log('showYearOverYear:', state.showYearOverYear) // 调试用

      const activeIndicator = state.indicators.find(i => i.key === state.activeIndicator)
      const hasValidData = indicatorData.some(item => item.value !== null && item.value !== undefined && item.value !== '')
      state.showImg = hasValidData
      // 动态确定Y轴单位
      const maxValue = Math.max.apply(Math, indicatorData.map(item => item.value || 0))
      let unit = '元'
      if (maxValue >= 1000000000000) {
        unit = '万亿'
      } else if (maxValue >= 100000000) {
        unit = '亿'
      } else if (maxValue >= 10000) {
        unit = '万'
      }

      // 根据图片样式优化图表
      const option = {
        title: {
          // text: activeIndicator?.title || '财务指标',
          left: 'center',
          textStyle: {
            fontSize: 16,
            fontWeight: 'bold',
            color: props.theme === 'dark' ? '#d7d7df' : '#333'
          }
        },
        tooltip: {
          show: true,
          trigger: 'item',
          formatter: function (params: any) {
            console.log('Tooltip params:', params) // 调试用
            if (!params) return '无数据'

            const date = params.name || '未知日期'
            const value = params.value
            const seriesName = params.seriesName

            if (value === null || value === undefined) return `${date}<br/>数值: --`

            // 获取当前数据点的所有数据
            const dataIndex = params.dataIndex
            const currentData = indicatorData[dataIndex]

            let tooltipContent = `${date}<br/>`

            // 显示数值数据
            if (currentData && currentData.value !== null && currentData.value !== undefined) {
              const formattedValue = formatValue(currentData.value)
              tooltipContent += `<span style="display: inline-block; width: 8px; height: 8px; background-color: #1890ff; margin-right: 6px;margin-top: -6px;"></span>${activeIndicator?.title}: ${formattedValue}<br/>`
            }

            // 显示同比数据（根据开关控制）
            if (state.showYearOverYear && currentData && currentData.yoy !== null && currentData.yoy !== undefined) {
              const formattedYoy = formatYoy(currentData.yoy)
              const yoyColor = currentData.yoy >= 0 ? '#FF0000' : '#26A230' // 红涨绿跌
              tooltipContent += `<span style="display: inline-block; width: 8px; height: 3px; 
              background-color: #FFB63C; margin-right: 6px;margin-bottom: 3px;
              "></span>同比: <span style="color: ${yoyColor};">${formattedYoy}</span><br/>`
            }

            return tooltipContent
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: indicatorData.map(item => item.dataTime),
          axisLine: {
            lineStyle: {
              color: props.theme === 'dark' ? '#252a34' : '#ccc'
            }
          },
          axisLabel: {
            interval: 0,
            rotate: 0,
            color: props.theme === 'dark' ? '#d7d7df' : '#333'
          }
        },
        yAxis: [
          {
            type: 'value',
            name: activeIndicator?.title || '财务指标',
            nameLocation: 'end',
            nameGap: 20,
            nameTextStyle: {
              color: props.theme === 'dark' ? '#d7d7df' : '#666'
            },
            axisLine: {
              show: true,
              lineStyle: {
                color: props.theme === 'dark' ? '#252a34' : '#ccc'
              }
            },
            splitLine: {
              lineStyle: {
                color: props.theme === 'dark' ? '#252a34' : '#f0f0f0',
                type: 'dashed'
              }
            },
            axisLabel: {
              color: props.theme === 'dark' ? '#d7d7df' : '#333',
              formatter: (value: number) => {
                if (unit === '万亿') {
                  return (value / 1000000000000).toFixed(2) + '万亿'
                } else if (unit === '亿') {
                  return (value / 100000000).toFixed(2) + '亿'
                } else if (unit === '万') {
                  return (value / 10000).toFixed(2) + '万'
                }
                return value.toFixed(2)
              }
            }
          },
          {
            type: 'value',
            name: '同比',
            nameLocation: 'end',
            nameGap: 20,
            nameTextStyle: {
              color: props.theme === 'dark' ? '#d7d7df' : '#666'
            },
            axisLine: {
              show: true,
              lineStyle: {
                color: props.theme === 'dark' ? '#252a34' : '#ccc'
              }
            },
            splitLine: {
              lineStyle: {
                color: props.theme === 'dark' ? '#252a34' : '#f0f0f0',
                type: 'dashed'
              }
            },
            axisLabel: {
              color: props.theme === 'dark' ? '#d7d7df' : '#333',
              formatter: (value: number) => {
                return formatYoy(value)
              }
            },
            // 使用show控制显示/隐藏
            show: state.showYearOverYear
          }
        ],
        series: [
          {
            name: '数值',
            type: 'bar',
            yAxisIndex: 0,
            barWidth: '30%',
            data: indicatorData.map(item => item.value),
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#1890ff' },
                { offset: 1, color: '#096dd9' }
              ])
            },
            emphasis: {
              itemStyle: {
                color: '#40a9ff'
              }
            },
            label: {
              show: true,
              position: 'top',
              formatter: (params: any) => {
                return formatValue(params.value)
              },
              color: props.theme === 'dark' ? '#d7d7df' : '#333',
              fontSize: 12,
              fontWeight: 'bold'
            }
          },
          {
            name: '同比',
            type: 'line',
            yAxisIndex: 1,
            data: state.showYearOverYear ? indicatorData.map(item => item.yoy) : [],
            lineStyle: {
              color: '#FFB63C',
              width: 2
            } as any,
            itemStyle: {
              color: '#FFB63C',
              borderWidth: 2
            } as any,
            symbol: 'circle',
            symbolSize: 6,
            label: {
              show: false,
              position: 'top',
              formatter: (params: any) => {
                return formatYoy(params.value)
              },
              color: '#FFB63C'
            },
            // 使用show控制显示/隐藏
            show: state.showYearOverYear
          } as any
        ]
      }

      state.chartInstance.setOption(option)
    }

    // 加载数据
    const loadData = () => {
      try {
        const market = isLocalDev ? route.query.market : (route.query.market as string)
        const symbol = isLocalDev ? route.query.code : (route.query.code as string)

        const data = {
          data: {
            market,
            symbol,
            tableType: '4',
            reportCate: state.reportCate
          },
          headers: {
            token: props.token || '3d5eff0d3c7ac7f3af30c337dcdf6944'
          }
        }

        f10ApiUS.getGwgk(data).then((res: any) => {
          if (res && res.data) {
            state.financialData = res.data.indexList || []
            state.spinning = false
            nextTick(() => {
              initChart()
            })
          }
          state.spinning = false
        }).catch(() => {
          state.spinning = false
        })
      } catch (error) {
        console.error('加载财务数据失败:', error)
        state.spinning = false
      }
    }

    onMounted(() => {
      loadData()
    })  // 根据标题宽度计算图标位置
    const getTooltipIconPosition = () => {
      const activeIndicator = state.indicators.find(i => i.key === state.activeIndicator)
      const title = activeIndicator?.title || '财务指标'

      // 基础位置 + 标题长度 * 字符宽度 + 间距
      const basePosition = 60 // Y轴标题起始位置
      const charWidth = 8 // 每个字符宽度
      const spacing = 28 // 图标与标题的间距
      console.log(title.length)
      return basePosition + (title.length * charWidth) + spacing
    }

    return {
      ...toRefs(state),
      reportTypes,
      reportPeriods,
      toggleYearOverYear,
      toggleSingleQuarter,
      toggleReportCate,
      selectIndicator,
      getIndicatorValue,
      getYoyValue,
      getYoyClass,
      formatValue,
      formatYoy,
      formatDate,
      getReportDate,
      getIndentClass,
      handleReportTypeChange,
      handleReportCateChange,
      getTooltipIconPosition
    }
  }
})
